import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/**
 * Campaigns list — version3's `routes/campaigns.index.tsx` and
 * `components/houdiny/CampaignCard.tsx`, class for class.
 * Radius scale: lg 14 · xl 18 · 2xl 22.
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const BORDER = 'var(--hd-border)';
const CARD = 'var(--hd-card)';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const StyledCampaigns = styled.div`
  ${brandTheme}
  color: ${FG};

  /* mx-auto max-w-[1480px] px-8 py-8 */
  .campaigns-main {
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;
  }

  /* flex flex-col gap-6 pb-8 */
  .campaigns-hero {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-bottom: 32px;
  }

  .hero-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: flex-end;
    }

    h1 {
      margin: 0;
      font-size: 24px;
      line-height: 32px;
      font-weight: 600;
      letter-spacing: -0.025em;
      text-transform: none;
      color: ${FG};
    }

    p {
      margin: 4px 0 0;
      font-size: 14px;
      line-height: 20px;
      color: ${MUTED_FG};
    }
  }

  .hero-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 12px;
  }

  .btn-outline,
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px;
    border-radius: 18px;
    font-size: 14px;
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;
  }

  .btn-outline {
    border: 1px solid ${BORDER};
    font-weight: 500;
    color: ${FG};
    background: ${CARD};
    transition: background 0.15s ease;

    &:hover {
      background: var(--hd-muted);
    }
  }

  .btn-primary {
    border: none;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  /* grid grid-cols-2 gap-4 lg:grid-cols-4 */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    @media (min-width: 1024px) {
      grid-template-columns: none;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
    }
  }

  /* surface-card animate-fade-up p-5 */
  .stat-card {
    padding: 20px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);
    animation: ${fadeUp} 0.5s ease-out both;
    transition:
      box-shadow 220ms ease,
      transform 220ms ease,
      border-color 220ms ease;
  }

  .stat-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .stat-label {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${MUTED_FG};
  }

  .stat-value {
    margin-top: 8px;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: ${FG};
  }

  .stat-icon {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 14px;
    color: var(--hd-primary);
    background: var(--hd-secondary);

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .stat-delta {
    min-height: 16px;
    margin-top: 12px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: #059669;
  }

  /* mb-5 flex items-center justify-between */
  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .section-label {
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${MUTED_FG};
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    font-size: 12px;
    line-height: 16px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);

    button {
      padding: 6px 12px;
      border: none;
      border-radius: 14px;
      font: inherit;
      color: ${MUTED_FG};
      background: transparent;
      cursor: pointer;
      transition: color 0.15s ease;

      &:hover {
        color: ${FG};
      }

      &.active {
        font-weight: 500;
        color: ${FG};
        background: var(--hd-muted);
      }
    }
  }

  /* grid grid-cols-1 gap-6 lg:grid-cols-2 */
  .campaigns-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;

    @media (min-width: 1024px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .campaigns-empty {
    padding: 48px 0;
    font-size: 14px;
    text-align: center;
    color: ${MUTED_FG};
  }
`;

/* version3 CampaignCard */
export const StyledCampaignCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 278px;
  overflow: hidden;
  padding: 28px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};
  box-shadow: var(--hd-shadow-card);
  cursor: ${({ $skeleton }) => ($skeleton ? 'default' : 'pointer')};
  animation: ${fadeUp} 0.5s ease-out both;
  transition:
    border-color 200ms ease,
    box-shadow 200ms ease,
    transform 200ms ease;

  &:hover {
    ${({ $skeleton }) =>
      !$skeleton &&
      `
      transform: translateY(-2px);
      border-color: color-mix(in srgb, var(--hd-primary) 20%, transparent);
      box-shadow: var(--hd-shadow-card-hover);
    `}
  }

  &:focus-visible {
    outline: 2px solid var(--hd-ring);
    outline-offset: 2px;
  }

  /* -right-16 -top-20 h-44 w-44 rounded-full tint opacity-35 blur-3xl */
  .card-glow {
    position: absolute;
    top: -80px;
    right: -64px;
    width: 176px;
    height: 176px;
    border-radius: 999px;
    background: ${({ $tint }) => $tint};
    opacity: 0.35;
    filter: blur(64px);
    pointer-events: none;
    transition: opacity 0.15s ease;
  }

  &:hover .card-glow {
    opacity: 0.55;
  }

  html.dark & .card-glow {
    opacity: 0.06;
  }

  html.dark &:hover .card-glow {
    opacity: 0.12;
  }

  .card-head {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  }

  .card-identity {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  /* Not .avatar / .avatar-wrap: GlobalStyles clips .avatar-wrap to a 36px circle. */
  .cc-avatar-wrap {
    position: relative;
    flex-shrink: 0;
    width: 56px;
    height: 56px;
  }

  .cc-avatar {
    display: block;
    max-width: none;
    width: 56px;
    height: 56px;
    border-radius: 22px;
    object-fit: cover;
    box-shadow: 0 0 0 4px ${({ $ring }) => $ring};
    transition: transform 0.15s ease;
  }

  &:hover .cc-avatar {
    transform: scale(1.03);
  }

  .status-dot {
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: ${({ $dot }) => $dot};
    box-shadow: 0 0 0 2px ${CARD};
  }

  .identity-text {
    min-width: 0;
  }

  && h3 {
    margin: 0;
    overflow: hidden;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-overflow: ellipsis;
    text-transform: none;
    white-space: nowrap;
    color: ${FG};
  }

  && .campaign-name {
    margin: 2px 0 0;
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${MUTED_FG};
  }

  .status-label {
    flex-shrink: 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  /* mt-6 grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-4 */
  .card-metrics {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 24px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .metric {
    min-width: 0;
  }

  && .metric-value {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: ${FG};
  }

  && .metric-label {
    margin: 2px 0 0;
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${MUTED_FG};
  }

  /* mt-auto flex items-center gap-2 border-t pt-5 */
  .card-actions {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: auto;
    padding-top: 20px;
    border-top: 1px solid ${BORDER};
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    font-size: 14px;
    font-weight: 500;
    color: ${FG};
    background: var(--hd-background);
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      background 0.15s ease;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover:not(:disabled) {
      background: var(--hd-muted);
    }

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }

    &.duplicate {
      display: none;

      @media (min-width: 640px) {
        display: inline-flex;
      }
    }

    &.icon-only {
      display: grid;
      flex-shrink: 0;
      place-items: center;
      width: 40px;
      padding: 0;
      color: ${MUTED_FG};

      &:hover:not(:disabled) {
        color: var(--hd-destructive);
      }
    }
  }

  &:hover .action-btn.edit {
    border-color: color-mix(in srgb, var(--hd-primary) 20%, transparent);
  }

  .open-btn {
    display: inline-flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 0;
    height: 40px;
    margin-left: auto;
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;

    svg {
      width: 16px;
      height: 16px;
      transition: transform 0.15s ease;
    }
  }

  &:hover .open-btn svg {
    transform: translateX(4px);
  }
`;
